import { useState, useEffect } from 'react';
import { unifiedApiService } from '../../services/unified';
import { ExperienceForm } from '.';
import { LoadingSpinner } from '../layout/LoadingSpinner';
import type { Experience, CreateExperienceRequest } from '../../types/profile';
import type { Experience as UnifiedExperience } from '../../services/unified/types';

// Adapter function to convert unified API experience to component experience
const adaptExperience = (exp: UnifiedExperience): Experience => ({
  id: exp.id,
  company: exp.company,
  title: exp.title,
  description: exp.description,
  startDate: exp.start_date,
  endDate: exp.end_date,
  current: !exp.end_date, // If no end date, assume current
  location: undefined, // Not in unified API yet
});

// Adapter function to convert component experience request to unified API format
const adaptExperienceRequest = (req: CreateExperienceRequest) => ({
  company: req.company,
  title: req.title,
  description: req.description,
  start_date: req.startDate,
  end_date: req.current ? undefined : req.endDate,
});

export function ExperienceSection() {
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingExperience, setEditingExperience] = useState<Experience | null>(null);

  useEffect(() => {
    loadExperiences();
  }, []);

  const loadExperiences = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await unifiedApiService.profile.getExperiences();
      setExperiences(data.map(adaptExperience));
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to load experience');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingExperience(null);
    setShowForm(true);
  };

  const handleEdit = (experience: Experience) => {
    setEditingExperience(experience);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this experience?')) {
      return;
    }

    try {
      await unifiedApiService.profile.deleteExperience(id);
      setExperiences(prev => prev.filter(exp => exp.id !== id));
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to delete experience');
    }
  };

  const handleFormSubmit = async (experienceData: CreateExperienceRequest) => {
    if (editingExperience) {
      const updated = await unifiedApiService.profile.updateExperience(
        editingExperience.id,
        adaptExperienceRequest(experienceData)
      );
      setExperiences(prev => prev.map(exp => exp.id === updated.id ? adaptExperience(updated) : exp));
    } else {
      const created = await unifiedApiService.profile.createExperience(adaptExperienceRequest(experienceData));
      setExperiences(prev => [...prev, adaptExperience(created)]);
    }
    setShowForm(false);
    setEditingExperience(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingExperience(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  };

  const calculateDuration = (startDate: string, endDate?: string) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) {
      return `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    } else if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      return `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-2">Work Experience</h2>
          <p className="text-sm text-gray-600">
            Add your work experience to showcase your professional background.
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Experience
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="mb-6 bg-gray-50 rounded-lg p-4">
          <ExperienceForm
            experience={editingExperience}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        </div>
      )}

      {experiences.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No work experience</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by adding your first work experience.</p>
          <div className="mt-6">
            <button
              onClick={handleAdd}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Add Experience
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {experiences.map((experience) => (
            <div key={experience.id} className="border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-medium text-gray-900">{experience.title}</h3>
                    {experience.current && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-md font-medium text-blue-600 mb-1">{experience.company}</p>
                  {experience.location && (
                    <p className="text-sm text-gray-500 mb-2">{experience.location}</p>
                  )}
                  <p className="text-sm text-gray-600 mb-3">
                    {formatDate(experience.startDate)} - {experience.current ? 'Present' : formatDate(experience.endDate!)} 
                    <span className="text-gray-400 ml-2">
                      ({calculateDuration(experience.startDate, experience.endDate)})
                    </span>
                  </p>
                  {experience.description && (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{experience.description}</p>
                  )}
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(experience)}
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(experience.id)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}